import React, { useState, useMemo, useEffect } from 'react';
import { X, Flag, Edit3, AlertCircle, CheckCircle2 } from 'lucide-react';
import api from '../utils/api';

/**
 * DataIssuesModal — Phase 1
 *
 * Modal con 2 tabs (REPORTAR / EDITAR) para que cualquier usuario reporte
 * problemas de datos o proponga ediciones a campos catalog-managed de un row.
 *
 * Spec: archive/PROPOSAL_DATA_ISSUES_MODAL_2026-05-11.md
 *
 * Phase 1 scope:
 *  - Tab REPORTAR: dropdown issue_category + field + textarea comment → POST type='report'
 *  - Tab EDITAR: form con campos editables → POST type='edit_proposal' (queda pending_approval)
 *  - NO ejecuta PATCH directo al catalog. La aprobación es Phase 2 (admin dashboard).
 */

interface DataIssuesModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: Record<string, any> | null;
  clientId?: string;
  tomaId?: string;
}

type Tab = 'report' | 'edit';

interface IssueCategoryOption {
  slug: string;
  label: string;
  group: 'data' | 'resource' | 'other';
}

const ISSUE_CATEGORIES: IssueCategoryOption[] = [
  { slug: 'dato_inconsistente', label: 'Dato inconsistente', group: 'data' },
  { slug: 'dato_faltante', label: 'Dato faltante', group: 'data' },
  { slug: 'dato_incorrecto', label: 'Dato incorrecto', group: 'data' },
  { slug: 'dato_ambiguo', label: 'Dato ambiguo', group: 'data' },
  { slug: 'precio_incorrecto', label: 'Precio incorrecto', group: 'data' },
  { slug: 'categorizacion_erronea', label: 'Mal categorizado', group: 'data' },
  { slug: 'duplicado', label: 'Producto duplicado', group: 'data' },
  { slug: 'url_rota', label: 'URL rota', group: 'resource' },
  { slug: 'url_incorrecta', label: 'URL incorrecta', group: 'resource' },
  { slug: 'imagen_rota', label: 'Imagen rota / errónea', group: 'resource' },
  { slug: 'otro', label: 'Otro (especificar)', group: 'other' },
];

// Campos editables (mismos que el PATCH /catalog/:idCatalogo acepta).
const EDITABLE_FIELDS: Array<{ key: string; label: string; aliases: string[] }> = [
  { key: 'catalog_brand', label: 'MARCA', aliases: ['MARCA', 'marca', 'Marca'] },
  { key: 'catalog_category', label: 'CATEGORIA', aliases: ['CATEGORIA', 'categoria', 'Cat'] },
  { key: 'segmento', label: 'SEGMENTO', aliases: ['SEGMENTO', 'segmento'] },
  { key: 'subcategoria', label: 'SUBCATEGORIA', aliases: ['SUBCATEGORIA', 'subcategoria'] },
  { key: 'contenido', label: 'CONTENIDO', aliases: ['CONTENIDO', 'contenido'] },
  { key: 'udm', label: 'UDM', aliases: ['UDM', 'udm'] },
];

// Campos que NO se ofrecen para reportar (infra / acciones / sistema).
const FIELDS_TO_EXCLUDE = new Set<string>([
  'actions',
  'selection',
  'comparisonId',
  'id_request_group',
  'fecha',
  'tags',
  'apariciones',
  'buybox_id',
  'buybox_winner',
  'imagen', // se reporta vía issue_category=imagen_rota
  '__rowId',
  'orderInPage',
]);

// Display names para los field keys raw del row.
// Mantener alineado con DataTable / catalog field labels.
const FIELD_DISPLAY_NAMES: Record<string, string> = {
  titulo: 'Título',
  title: 'Título',
  TITULO: 'Título',
  marca: 'MARCA',
  Marca: 'MARCA',
  MARCA: 'MARCA',
  catalog_brand: 'MARCA',
  Cat: 'CATEGORIA',
  cat: 'CATEGORIA',
  CATEGORIA: 'CATEGORIA',
  categoria: 'CATEGORIA',
  catalog_category: 'CATEGORIA',
  subcategoria: 'SUBCATEGORIA',
  SUBCATEGORIA: 'SUBCATEGORIA',
  tag_subcategoria: 'SUBCATEGORIA',
  segmento: 'SEGMENTO',
  SEGMENTO: 'SEGMENTO',
  contenido: 'CONTENIDO',
  CONTENIDO: 'CONTENIDO',
  udm: 'UDM',
  UDM: 'UDM',
  sale_price: 'Precio de venta',
  original_price: 'Precio original',
  discounted_price: 'Precio con descuento',
  precio: 'Precio',
  sitio: 'Sitio',
  SITIO: 'Sitio',
  url: 'URL',
  URL: 'URL',
  seller: 'Vendedor',
  Seller: 'Vendedor',
  cuotas: 'Cuotas',
  installements_value: 'Valor cuota',
  page: 'Página',
  id_catalogo: 'id_catalogo',
  ID_CATALOGO: 'id_catalogo',
};

function displayNameForField(key: string): string {
  return FIELD_DISPLAY_NAMES[key] || key;
}

function getValueFromRow(row: Record<string, any>, fieldKey: string, aliases: string[] = []): any {
  if (!row) return undefined;
  if (row[fieldKey] !== undefined && row[fieldKey] !== null) return row[fieldKey];
  for (const a of aliases) {
    if (row[a] !== undefined && row[a] !== null) return row[a];
  }
  return undefined;
}

function getProductTitle(row: Record<string, any> | null): string {
  if (!row) return '';
  return row.title || row.TITULO || row.titulo || row.Title || '';
}

function getIdCatalogo(row: Record<string, any> | null): string {
  if (!row) return '';
  return String(row.id_catalogo || row.ID_CATALOGO || row.id || '');
}

const DataIssuesModal: React.FC<DataIssuesModalProps> = ({
  isOpen,
  onClose,
  row,
  clientId,
  tomaId,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('report');

  // ---- Tab REPORTAR state ----
  const [issueCategory, setIssueCategory] = useState<string>('dato_inconsistente');
  const [reportFields, setReportFields] = useState<string[]>([]); // multiselect
  const [reportComment, setReportComment] = useState<string>('');

  // ---- Tab EDITAR state ----
  const [editField, setEditField] = useState<string>(EDITABLE_FIELDS[0].key);
  const [editValue, setEditValue] = useState<string>('');
  const [editComment, setEditComment] = useState<string>('');

  // ---- UI state ----
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  // Reset al abrir
  useEffect(() => {
    if (isOpen) {
      setActiveTab('report');
      setIssueCategory('dato_inconsistente');
      setReportFields([]);
      setReportComment('');
      setEditField(EDITABLE_FIELDS[0].key);
      setEditValue('');
      setEditComment('');
      setSubmitting(false);
      setSubmitError(null);
      setSubmitSuccess(null);
    }
  }, [isOpen]);

  // Cuando cambia el field editable, pre-cargar el valor actual
  useEffect(() => {
    if (!row) return;
    const def = EDITABLE_FIELDS.find(f => f.key === editField);
    if (def) {
      const v = getValueFromRow(row, def.key, def.aliases);
      setEditValue(v !== undefined && v !== null ? String(v) : '');
    }
  }, [editField, row]);

  // Lista de fields disponibles para REPORTAR (derivados del row).
  // Dedup por display name (para que MARCA / marca / Marca no aparezcan 3 veces).
  const reportableFields = useMemo(() => {
    if (!row) return [] as Array<{ key: string; label: string }>;
    const keys = Object.keys(row).filter(k => {
      if (FIELDS_TO_EXCLUDE.has(k)) return false;
      if (k.startsWith('_')) return false;
      const v = row[k];
      const t = typeof v;
      return t === 'string' || t === 'number' || t === 'boolean' || v === null || v === undefined;
    });
    // Para cada display name, quedarse con el primer key encontrado.
    const seenLabels = new Map<string, string>();
    for (const k of keys) {
      const label = displayNameForField(k);
      if (!seenLabels.has(label)) seenLabels.set(label, k);
    }
    return Array.from(seenLabels.entries())
      .map(([label, key]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [row]);

  const toggleReportField = (key: string) => {
    setReportFields(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  if (!isOpen || !row) return null;

  const idCatalogo = getIdCatalogo(row);
  const productTitle = getProductTitle(row);

  // Snapshot del producto para enviar al backend
  const buildProductSnapshot = () => ({
    title: row.title || row.TITULO || row.titulo || null,
    sitio: row.sitio || row.SITIO || null,
    sale_price: row.sale_price ?? row.SALE_PRICE ?? null,
    url: row.url || row.URL || null,
    imagen: row.imagen || row.IMAGEN || null,
  });

  // ---- Submit REPORTAR ----
  const handleSubmitReport = async () => {
    setSubmitError(null);
    setSubmitSuccess(null);

    if (!clientId) {
      setSubmitError('Falta clientId — no se puede enviar el reporte.');
      return;
    }
    if (!idCatalogo) {
      setSubmitError('Este row no tiene id_catalogo.');
      return;
    }
    if (reportFields.length === 0) {
      setSubmitError('Seleccioná al menos un campo afectado.');
      return;
    }
    if (issueCategory === 'otro' && !reportComment.trim()) {
      setSubmitError('Si elegís "Otro", el comentario es obligatorio.');
      return;
    }

    setSubmitting(true);
    try {
      // Mandamos `fields` (array). El backend crea N issues, uno por field.
      // `current_value` se omite a nivel top — cada doc del backend usa su propio
      // current_value=null o lo que mande front; acá no hace sentido pasarlo si son varios.
      const resp = await api.post(`/api/clients/${clientId}/data-issues`, {
        id_catalogo: idCatalogo,
        type: 'report',
        issue_category: issueCategory,
        fields: reportFields,
        current_value: reportFields.length === 1 ? (row[reportFields[0]] ?? null) : null,
        proposed_value: null,
        comment: reportComment.trim() || null,
        product_snapshot: buildProductSnapshot(),
        tomaId: tomaId || null,
      });
      const n = resp.data?.count || reportFields.length;
      setSubmitSuccess(
        n === 1
          ? `Reporte enviado · #${resp.data?.issueId || 'ok'}`
          : `${n} reportes enviados`
      );
      setTimeout(() => onClose(), 1200);
    } catch (e: any) {
      setSubmitError(e?.response?.data?.error || e?.message || 'Error al enviar reporte');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Submit EDITAR ----
  const handleSubmitEdit = async () => {
    setSubmitError(null);
    setSubmitSuccess(null);

    if (!clientId) {
      setSubmitError('Falta clientId — no se puede enviar la propuesta.');
      return;
    }
    if (!idCatalogo) {
      setSubmitError('Este row no tiene id_catalogo.');
      return;
    }
    if (!editValue.trim()) {
      setSubmitError('Ingresá el valor propuesto.');
      return;
    }

    const fieldDef = EDITABLE_FIELDS.find(f => f.key === editField);
    if (!fieldDef) {
      setSubmitError('Campo inválido.');
      return;
    }

    const currentValue = getValueFromRow(row, fieldDef.key, fieldDef.aliases);

    setSubmitting(true);
    try {
      const resp = await api.post(`/api/clients/${clientId}/data-issues`, {
        id_catalogo: idCatalogo,
        type: 'edit_proposal',
        issue_category: null,
        field: fieldDef.key,
        current_value: currentValue ?? null,
        proposed_value: editValue.trim(),
        comment: editComment.trim() || null,
        product_snapshot: buildProductSnapshot(),
        tomaId: tomaId || null,
      });
      setSubmitSuccess(`Propuesta enviada · #${resp.data?.issueId || 'ok'} · pendiente de aprobación`);
      setTimeout(() => onClose(), 1500);
    } catch (e: any) {
      setSubmitError(e?.response?.data?.error || e?.message || 'Error al enviar propuesta');
    } finally {
      setSubmitting(false);
    }
  };

  // Render dropdown de issue_category agrupado
  const renderCategorySelect = () => {
    const grouped = {
      data: ISSUE_CATEGORIES.filter(c => c.group === 'data'),
      resource: ISSUE_CATEGORIES.filter(c => c.group === 'resource'),
      other: ISSUE_CATEGORIES.filter(c => c.group === 'other'),
    };
    return (
      <select
        value={issueCategory}
        onChange={(e) => setIssueCategory(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
      >
        <optgroup label="Calidad del dato">
          {grouped.data.map(c => (
            <option key={c.slug} value={c.slug}>{c.label}</option>
          ))}
        </optgroup>
        <optgroup label="Recursos">
          {grouped.resource.map(c => (
            <option key={c.slug} value={c.slug}>{c.label}</option>
          ))}
        </optgroup>
        <optgroup label="Otro">
          {grouped.other.map(c => (
            <option key={c.slug} value={c.slug}>{c.label}</option>
          ))}
        </optgroup>
      </select>
    );
  };

  // Valor actual mostrado bajo el field elegido en tab EDITAR
  const editFieldDef = EDITABLE_FIELDS.find(f => f.key === editField);
  const currentValueForEdit = editFieldDef
    ? getValueFromRow(row, editFieldDef.key, editFieldDef.aliases)
    : undefined;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold text-gray-900">
              Reportar / Editar producto
            </h2>
            <p className="mt-1 text-xs text-gray-500 truncate">
              {productTitle ? `${productTitle} · ` : ''}
              <span className="font-mono">id_catalogo: {idCatalogo || '—'}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 ml-4"
            aria-label="Cerrar modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 border-b border-gray-200">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('report')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-t-md transition-colors ${
                activeTab === 'report'
                  ? 'bg-blue-50 text-blue-700 border border-blue-200 border-b-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Flag className="h-4 w-4" />
              Reportar problema
            </button>
            <button
              onClick={() => setActiveTab('edit')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-t-md transition-colors ${
                activeTab === 'edit'
                  ? 'bg-pink-50 text-pink-700 border border-pink-200 border-b-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Edit3 className="h-4 w-4" />
              Editar dato
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Feedback banner */}
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{submitError}</span>
            </div>
          )}
          {submitSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700 flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{submitSuccess}</span>
            </div>
          )}

          {/* Tab REPORTAR */}
          {activeTab === 'report' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de issue <span className="text-red-500">*</span>
                </label>
                {renderCategorySelect()}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campos afectados <span className="text-red-500">*</span>
                  <span className="text-gray-400 font-normal text-xs ml-1">
                    (podés elegir varios — se crea un reporte por cada uno)
                  </span>
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-2 bg-white">
                  {reportableFields.length === 0 ? (
                    <p className="text-xs text-gray-400 italic px-1 py-2">
                      No hay campos disponibles en este row.
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                      {reportableFields.map(f => {
                        const checked = reportFields.includes(f.key);
                        const v = row[f.key];
                        return (
                          <label
                            key={f.key}
                            className={`flex items-start gap-1.5 px-1.5 py-1 rounded cursor-pointer text-sm ${
                              checked ? 'bg-blue-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleReportField(f.key)}
                              className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-800">{f.label}</div>
                              <div className="text-[10px] text-gray-400 font-mono truncate">
                                {v === null || v === undefined || v === ''
                                  ? '(vacío)'
                                  : String(v).slice(0, 30)}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                {reportFields.length > 0 && (
                  <p className="mt-1 text-xs text-blue-600">
                    {reportFields.length} campo{reportFields.length === 1 ? '' : 's'} seleccionado{reportFields.length === 1 ? '' : 's'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comentario {issueCategory === 'otro' && <span className="text-red-500">*</span>}
                  {issueCategory !== 'otro' && <span className="text-gray-400 font-normal"> (opcional)</span>}
                </label>
                <textarea
                  value={reportComment}
                  onChange={(e) => setReportComment(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describí el problema..."
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                <button
                  onClick={onClose}
                  disabled={submitting}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmitReport}
                  disabled={submitting || reportFields.length === 0}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting
                    ? 'Enviando...'
                    : reportFields.length > 1
                      ? `Enviar ${reportFields.length} reportes`
                      : 'Enviar reporte'}
                </button>
              </div>
            </div>
          )}

          {/* Tab EDITAR */}
          {activeTab === 'edit' && (
            <div className="space-y-4">
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                Tu propuesta queda <strong>pendiente de aprobación</strong> por un admin. No se aplica directamente al catálogo.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Campo <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={editField}
                    onChange={(e) => setEditField(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-pink-500 focus:border-pink-500"
                  >
                    {EDITABLE_FIELDS.map(f => (
                      <option key={f.key} value={f.key}>
                        {/* `EDITABLE_FIELDS` ya usa labels human-friendly (MARCA, CATEGORIA…) */}
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor propuesto <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-pink-500 focus:border-pink-500"
                    placeholder="Ingresá el nuevo valor"
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500 font-mono break-all -mt-2">
                Actual: "{currentValueForEdit ?? ''}"
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comentario <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <textarea
                  value={editComment}
                  onChange={(e) => setEditComment(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-pink-500 focus:border-pink-500"
                  placeholder="Explicá por qué proponés este cambio..."
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                <button
                  onClick={onClose}
                  disabled={submitting}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmitEdit}
                  disabled={submitting || !editValue.trim()}
                  className="px-4 py-2 text-sm text-white bg-pink-600 rounded-md hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Enviando...' : 'Proponer edición'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataIssuesModal;
